import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';

// Types
import { ExternalCompanyManagement, Props } from './types';

// Schema
import { ExternalCompanyManagementSchema } from './schema';

// Styles
import { AddExternalCompanyButton, Container, InputsContainer } from './styles';

// Components
import InputText from '../../inputs/InputText';
import { Typography } from '../../display/Typography';

// Utils
import { useCacheFormInSessionStorage } from '../../../hooks/useCacheForm';

/**
 * Alta de empresa externa desde el módulo de Gestión.
 *
 * Un único campo (nombre). La validación de duplicado la hace la página, que
 * es quien tiene el catálogo completo; el backend la repite como backstop.
 */
export const AddExternalCompanyForm = ({
  onFormSubmit,
  namingPersistForm,
  initialData,
}: Props) => {
  const { t } = useTranslation(['pages', 'forms']);
  const {
    handleSubmit,
    register,
    control,
    formState: { errors },
    reset,
  } = useForm<ExternalCompanyManagement>({
    defaultValues: { companyName: '', ...initialData },
    resolver: yupResolver(ExternalCompanyManagementSchema) as any,
  });
  useCacheFormInSessionStorage(namingPersistForm, control);

  const [companyError, setCompanyError] = useState<string | null>(null);

  const onSubmit = async (data: ExternalCompanyManagement) => {
    const errorMessage = await onFormSubmit(data);
    if (errorMessage) {
      setCompanyError(errorMessage);
      return;
    }
    reset({ companyName: '' });
    setCompanyError(null);
    // Formulario de alta puntual: no tiene sentido persistir el borrador tras
    // crear (mismo criterio que AddMachineryManagementForm).
    sessionStorage.removeItem(namingPersistForm);
  };

  // El form vive dentro de la página de Gestión; preventDefault evita que el
  // submit burbujee a cualquier form padre.
  const handleButtonClick = (e: React.MouseEvent) => {
    e.preventDefault();
    handleSubmit(onSubmit)();
  };

  return (
    <Container>
      <Typography size="L" variant="semiBold">
        {t('pages:management.addExternalCompany')}
      </Typography>

      <InputsContainer>
        <InputText
          {...register('companyName')}
          label={t('forms:company.name')}
          typeInput={'normal'}
          inputSize="S"
          placeholder={t('forms:company.namePlaceholder')}
          borderError={!!errors.companyName?.message}
        />
        {/*
          aria-label: el botón solo contiene una flecha, así que sin esto no
          tiene nombre accesible — y además colisiona con el ⮕ de desactivar
          de la tabla.
        */}
        <AddExternalCompanyButton
          type="button"
          aria-label={t('pages:management.addExternalCompany')}
          title={t('pages:management.addExternalCompany')}
          onClick={handleButtonClick}
        >
          ⮕
        </AddExternalCompanyButton>
      </InputsContainer>

      {companyError && (
        <Typography colorText="lightCoralRed" size="XS">
          {companyError}
        </Typography>
      )}
    </Container>
  );
};
